import { dateFormat } from '@constants/dateFormat'
import { GetAmEndDate, GetAmStartDate, GetPmEndDate, GetPmStartDate } from '@utils/general'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

import type { DriverScheduleParameters } from './types'

dayjs.extend(utc)

export const getDriverScheduleCounts = (drivers, driverSchedule) => {
  const driverScheduleCopy = [...driverSchedule]
  driverScheduleCopy.forEach((date) => {
    date.am_driver_allocated = 0
    date.pm_driver_allocated = 0
  })
  drivers.forEach((driver) => {
    driver.schedule.forEach((scheduleDate, index) => {
      if (scheduleDate.events.some((event) => event.type === 'am shift')) {
        driverScheduleCopy[index].am_driver_allocated += 1
      }
      if (scheduleDate.events.some((event) => event.type === 'pm shift')) {
        driverScheduleCopy[index].pm_driver_allocated += 1
      }
    })
  })
  return driverScheduleCopy
}

export const SCHEDULE_EDITING_MODES = {
  SINGLE: 'single',
  BULK: 'bulk',
  TEMPLATE: 'template',
} as const

export type ScheduleEditingMode = (typeof SCHEDULE_EDITING_MODES)[keyof typeof SCHEDULE_EDITING_MODES]

export const SHIFT_TYPES = {
  ALL: 'all',
  AM: 'am',
  PM: 'pm',
} as const

export type ShiftType = (typeof SHIFT_TYPES)[keyof typeof SHIFT_TYPES]

export function parseTemplateName(templateName: string, startDate: string, endDate: string) {
  const suffix = templateName.slice(-2)
  const type = suffix === 'AM' || suffix === 'PM' ? suffix : ''

  const daysOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

  // Extract start and end days from the input string
  const [startDay, , endDay] = templateName.split(' ')

  // Get index of each day in the week
  const startIndex = daysOfWeek.indexOf(startDay.toLowerCase())
  const endIndex = daysOfWeek.indexOf(endDay.toLowerCase())

  // return default response if not found
  if (startIndex === -1 || endIndex === -1) {
    return {
      date: addDaysAndFormat(startDate, 0, ''),
      start_date: addDaysAndFormat(startDate, 0, type, true),
      end_date: addDaysAndFormat(endDate, 5, type, false),
      days_off: 2,
      days_on: 5,
      type,
    } as Partial<DriverScheduleParameters>
  }

  // Calculate difference in days
  const diff = 7 - ((endIndex - startIndex + 1 + 7) % 7)

  return {
    date: addDaysAndFormat(startDate, startIndex, ''),
    start_date: addDaysAndFormat(startDate, startIndex, type, true),
    end_date:
      startIndex === 5 && endIndex === 1
        ? addDaysAndFormat(endDate, 4, type, false)
        : addDaysAndFormat(endDate, 5, type, false),
    days_off: diff,
    days_on: 7 - diff,
    type,
  } as Partial<DriverScheduleParameters>
}

function addDaysAndFormat(date: string, days: number, type: DriverScheduleParameters['type'], isStart?: boolean) {
  const formattedDate = dayjs(date).utc().add(days, 'd').startOf('day').format(dateFormat.ISO)
  if (type === 'PM') {
    return isStart ? GetPmStartDate(formattedDate) : GetPmEndDate(formattedDate)
  }
  if (type === 'AM') {
    return isStart ? GetAmStartDate(formattedDate) : GetAmEndDate(formattedDate)
  }
  return formattedDate
}
